import * as THREE from 'three';
import { Visualizer } from '../visualizer.js';
import { safeZoneUniforms, SAFE_ZONE_VERTEX_CHUNK } from '../shaders/safeZone.glsl.js';
import { getMaxPointSize } from '../scene-manager.js';
import {
  laserClampChunk,
  laserBillboardChunk,
  laserBeamFragmentShader,
  laserImpactFragmentShader,
  laserDustFragmentShader
} from '../lighting/laser-array.js';

// Shared-rig contract (see the framing addendum). 'forbidden', for the same
// reason as laser-cathedral.js: the deck stays black, because a wash would
// destroy exactly the contrast the beams depend on to read as light.
const RIG = 'forbidden';
export { RIG };

// Orchestrator registry entry (see visualizer-registry.js).
export const PROFILE = {
  id: 'lasergrid',
  family: 'stage',
  energyLevel: 0.88,
  bpmMin: 120,
  bpmMax: 185,
  movement: 0.85,
  // 0.80, not the 0.88 it launched with: the danceability pool picks the
  // profiles NEAREST the track's own index, and real tracks essentially
  // never index above the low 0.8s - at 0.88 this entry sat past every
  // track and never made the pool at all ("sinon ca joue jamais"). 0.80
  // parks it in the same tier as lasercathedral/kineticrig, where it
  // actually competes.
  danceability: 0.80,
  tonality: 0.55,
  prefers: { gridStrength: [0.60, 1.0], lowEnd: [0.45, 1.0] },
  enabled: true
};

// LaserGrid: SHEETS of parallel beams, crossed at eye height.
//
// The difference from laser-cathedral.js is the geometry of the light. The
// cathedral projects from a point above the deck: its beams DIVERGE and land
// on the floor, and the viewer stands in front of the plot. Here every sheet
// is a stack of rigorously parallel beams spread across the visible frame -
// A and B are horizontal beams stacked over the frame's HEIGHT, C is vertical
// beams combed across its WIDTH - and the camera sits among them. Nothing
// ever converges, and crossed sheets make intersection figures the vertical
// version cannot.
//
// THE SPREAD IS COMPUTED, NEVER WRITTEN DOWN. The first build spread the
// horizontal sheets along DEPTH - a plane seen edge-on - and fourteen beams
// collapsed into one thick blade on screen. A sheet now covers a fixed
// fraction of the visible perpendicular dimension at its own depth
// (SHEET_COVERAGE, from the camera's live fov/aspect, so resizes are
// automatically right), and bands.low only MODULATES that by +/-25%: the
// base can never be zero.
//
// PARALLELISM IS THE EFFECT. A sheet's beams share one direction vector by
// construction (it depends only on the sheet index, never on the beam
// index); nothing in any gesture rotates a beam individually. SHEAR staggers
// beams in depth - a deck of cards spread and squared up - without breaking
// the stack.
//
// EVERY BEAM ENDS ON A SURFACE. A/B are angled toward the back wall and
// their length is DERIVED so they land exactly on it (vertical impact quads
// there); C's beams run exactly to the floor (flat impact quads there). The
// shared beam fade is set later than the cathedral's (BEAM_FADE_* below):
// the cathedral dissolves its beams into haze, but a beam that ends on a
// wall has to still be carrying light when it arrives.
//
// Beam rendering (pixel-width floor, near-white core, coloured halo, all
// intensity in rgb with alpha 1, camera-axis guard) comes from the shared
// LaserArray module, extracted from the cathedral precisely so this file
// would not rewrite it.
//
// THE FLOOR REFLECTS. The camera sits at eye level, so everything below the
// horizon is the near-black floor - without a reflection the whole bottom
// band of the frame stays dead. Beams and intersection dots are drawn a
// second time mirrored through y = 0, dimmed and widened (the floor's 0.78
// roughness is not a mirror), with depth testing off: a virtual image below
// the floor plane always projects below the horizon, which is exactly the
// region that was black, and the floor's own depth would otherwise cull it.
//
// PERFORMANCE SHAPE, tier 2: 42 beams in one instanced draw (x2 with the
// reflection), one THREE.Points for the intersections (x2), 42 impact quads,
// 600 dust points. Six draw calls for the light, plus the floor. Zero
// per-frame attribute uploads: every gesture is a handful of scalar uniforms.

const CAMERA_POS = [0, 2.4, 7];
const CAMERA_LOOK = [0, 2.4, -3];
const EYE_Y = 2.4;
// Softer than the cathedral's 0.055, per the brief.
const FOG_DENSITY = 0.05;
const FLOOR_SIZE = 60;
const FLOOR_COLOR = 0x0a0c10;

// --- sheet geometry ---------------------------------------------------------
// A and B: stacks of horizontal beams, one origin line each at x = -/+EMIT_X,
// z = Z_AB_ORIGIN, heights spread over the stack's span. Angled toward the
// back wall so every beam lands on it - and because all beams of a stack
// share one direction and one origin depth, they all land at the SAME x: a
// vertical column of impact dots that rides the sweep.
const EMIT_X = 13;
const SHEET_ANGLE = 0.5;           // radians off the x axis, toward -z
const SIN_ANGLE = Math.sin(SHEET_ANGLE);
const COS_ANGLE = Math.cos(SHEET_ANGLE);
const TAN_ANGLE = SIN_ANGLE / COS_ANGLE;
const WALL_Z = -12;
const Z_AB_ORIGIN = 1.5;
// Derived, never written down: the beam ends exactly ON the wall. The first
// build's hardcoded length was one of the two reasons beams stopped mid-air.
// NOTE: this is only the UNSHEARED length. SHEAR staggers a stack's beams in
// depth, which moves each beam's own wall distance - the shader derives the
// real length per beam from its origin (see sheetLen), because a constant
// here left staggered beams stopping in mid-air or piercing the wall, both
// without their arrival dot.
const BEAM_LEN_AB = (Z_AB_ORIGIN - WALL_Z) / SIN_ANGLE;   // ~28.2, at shear 0
// C: a vertical curtain, beams straight down from above the frame, combed
// across x. Its animated parameter is the whole comb's LATERAL position. Each
// beam's DEPTH follows uCurtainZ (base + slope * (x + EMIT_X)): flat at
// CURTAIN_Z normally, and in GRID tilted onto sheet A's own slanted plane -
// which is what makes every apparent crossing there a TRUE 3D crossing.
const CURTAIN_TOP_Y = 9.5;
const BEAM_LEN_C = 9.5;            // ends exactly at the floor
const CURTAIN_Z = -4;

// The correction's own formula: spacing = COVERAGE * visibleDim / (N - 1).
// ?gridspread=<float> replaces COVERAGE live for tuning.
const SHEET_COVERAGE = 0.70;
const COVERAGE_SWING = 0.25;       // bands.low modulates, +/-25% only
// Reference depths the visible dimension is measured at - the middle of where
// each sheet's beams cross the frame (the camera's z is fixed at 7 above).
const AB_REF_DEPTH = 9;
const C_REF_DEPTH = 11;

// Heights the horizontal stacks sweep over, either side of eye level.
const SWEEP_MID = EYE_Y;
const SWEEP_AMP = 2.0;
const LATERAL_AMP = 3.2;           // curtain sweep, either side of centre

// SHEAR: how far the fan staggers a stack's beams in depth at frac01 = 1.
const SHEAR_SPAN = 1.6;

// COMB: trailing comet behind the playhead beam, in beams.
const COMB_TRAIL_DECAY = 0.55;

// COLLAPSE: two bars - converge to a point at the centre, then blow outward.
const COLLAPSE_BARS = 2;
const COLLAPSE_POINT_Y = EYE_Y;
const COLLAPSE_EXPLODE = 2.6;      // span multiplier at the peak of the burst

// Beam body. Same divergence as the cathedral's; the halo is slimmer (10px
// against 18) because fourteen of these stack in one frame dimension and
// each must stay visually separate from its neighbours - the correction asks
// for at least 4x the beam's thickness of clear space between them, and at
// 12px the measured half-max thickness (bloom included) sat right on that
// boundary, crossing it or not with the pulse's phase at capture time.
const BEAM_HALF_WIDTH_NEAR = 0.035 / 2;
const BEAM_HALF_WIDTH_FAR = 0.075 / 2;
const BEAM_OUTER_PX = 10.0;
const BEAM_CORE_PX = 3.0;
const BEAM_HOT_MIX = 0.95;         // the brief's own number, hotter than 0.85
const PEAK_LUMINANCE = 0.92;
// Where along its length a beam starts and finishes fading (see the header):
// still ~68% bright at vLen = 1.0, where its impact quad marks the arrival.
const BEAM_FADE_START = '0.85';
const BEAM_FADE_END = '1.25';

// The floor reflection: how much of the light comes back, and how much wider
// the reflected beam reads (roughness smears it).
const REFLECT_GAIN = 0.28;
const REFLECT_OUTER_MULT = 2.2;

// The pulse the brief specifies: computeBeatPulse(0.6, 13) via director.pulse.
const BEAT_PULSE_AMOUNT = 0.6;
const BEAT_PULSE_DECAY = 13;

// --- intersections ----------------------------------------------------------
// One slot per (pair sheet, stack beam, curtain beam) triple: the dot sits
// where curtain beam j crosses the height of stack beam k, and it LIGHTS only
// where the two beams genuinely pass through the same point in 3D - the
// curtain beam's depth against the stack's slanted plane, all analytic from
// the same uniforms the beams are built from. In GRID the curtain is tilted
// onto sheet A's plane, so the whole lattice lights at once.
//
// Slots are 2 * N * N (392 vertices at tier 2). The original 40-point budget
// cannot describe a 14x14 grid of crossings, and a THREE.Points of a few
// hundred vertices costs nothing - that is the one deliberate departure from
// the first brief's numbers.
const INTERSECTION_WORLD_SIZE = 0.34;
const INTERSECTION_TOL_Z = 0.55;   // world units off the plane before a dot dies
const HIGH_MIN = 0.45;             // bands.high -> intersection intensity
const HIGH_MAX = 1.45;

// --- impacts ----------------------------------------------------------------
const IMPACT_BASE_SIZE = 0.45;
const IMPACT_HOT_MIX = 0.85;

// --- dust -------------------------------------------------------------------
const DUST_REVEAL_RADIUS = 0.85;
const DUST_POINT_WORLD_SIZE = 0.09;
const DUST_POINT_WORLD_VARIANCE = 0.05;
const DUST_BRIGHTNESS = 1.9;
const DUST_FIELD = { halfX: 9, yMin: 0.3, yMax: 6.5, zMin: -10, zMax: 4 };

// --- palette ----------------------------------------------------------------
const LASER_SATURATION = 0.95;     // the brief's own figure
const LASER_LIGHTNESS = 0.55;
const FALLBACK_COLORS = [0xff2020, 0x20ff40, 0x2060ff];

// --- gestures ---------------------------------------------------------------
const MODE_SCAN = 0;
const MODE_GRID = 1;
const MODE_SHEAR = 2;
const MODE_COMB = 3;
const MODE_COLLAPSE = 4;
const MODE_NAMES = { scan: MODE_SCAN, grid: MODE_GRID, shear: MODE_SHEAR, comb: MODE_COMB, collapse: MODE_COLLAPSE };
// COLLAPSE is rare by the brief's own odds: one pick in eight (measured 13.8%
// over 8000 draws with these weights).
const MODE_WEIGHTS = [
  { mode: MODE_SCAN, w: 2.5 },
  { mode: MODE_GRID, w: 2.0 },
  { mode: MODE_SHEAR, w: 1.5 },
  { mode: MODE_COMB, w: 1.0 },
  { mode: MODE_COLLAPSE, w: 1.0 }
];
// Fallbacks for when the director has no grid: a synthetic bar and a mode
// change on a fixed clock, so radio streams still get the full show.
const NO_GRID_BAR_S = 2.0;
const NO_GRID_MODE_S = 14.0;
const NO_GRID_BEAT_S = 0.5;

const TIER_LAYOUT = {
  0: { perSheet: 8, sheets: 2, intersections: false, dustCount: 0 },
  1: { perSheet: 10, sheets: 3, intersections: true, dustCount: 350 },
  2: { perSheet: 14, sheets: 3, intersections: true, dustCount: 600 }
};
const DEFAULT_TIER = 1;

const TAU_SPACING = 0.6;           // bands.low smoothing
const TAU_HIGH = 0.25;
const TAU_CURTAIN_GAIN = 1.4;      // progress fades the third sheet in and out
const TAU_CURTAIN_POSE = 1.1;      // the curtain glides between flat and tilted

function clamp01(x) { return Math.max(0, Math.min(1, x)); }
function lerp(a, b, t) { return a + (b - a) * t; }
function approach(current, target, dt, tau) {
  return current + (target - current) * (1 - Math.exp(-dt / Math.max(tau, 1e-4)));
}
function hueDistance(a, b) {
  const d = Math.abs(a - b) % 1;
  return d > 0.5 ? 1 - d : d;
}

const CLAMP_CHUNK = laserClampChunk(PEAK_LUMINANCE);
const BILLBOARD_CHUNK = laserBillboardChunk({
  halfWidthNear: BEAM_HALF_WIDTH_NEAR,
  halfWidthFar: BEAM_HALF_WIDTH_FAR
});

// Everything a sheet is, in one chunk shared by the beam, impact, point and
// dust shaders - the same single-source-of-truth role BEAM_DIR_CHUNK plays in
// the cathedral. A sheet's direction depends ONLY on the sheet index: that is
// the parallelism criterion, enforced structurally.
const SHEET_CHUNK = `
  uniform float uBeamsPerSheet;
  uniform vec3 uSheetPos;     // (heightA, heightB, curtainX)
  uniform vec3 uShear;        // per-sheet depth stagger, world units at frac01 = 1
  uniform vec2 uSpan;         // HALF-spans: (A/B over y, C over x)
  uniform vec3 uSheetGain;
  uniform vec2 uCurtainZ;     // (base, slope): z(x) = base + slope * (x + EMIT)
  uniform float uCombSheet;   // -1: comb off. Otherwise the only lit sheet.
  uniform float uCombHead;    // playhead, in beam indices

  const float SIN_A = ${SIN_ANGLE.toFixed(6)};
  const float COS_A = ${COS_ANGLE.toFixed(6)};
  const float EMIT = ${EMIT_X.toFixed(1)};
  const float Z_AB = ${Z_AB_ORIGIN.toFixed(1)};

  // One direction per SHEET - never per beam.
  vec3 sheetDir(float s) {
    if (s < 0.5) return vec3(COS_A, 0.0, -SIN_A);
    if (s < 1.5) return vec3(-COS_A, 0.0, -SIN_A);
    return vec3(0.0, -1.0, 0.0);
  }

  // The curtain's per-beam depth - see uCurtainZ.
  float curtainZAt(float x) {
    return uCurtainZ.x + uCurtainZ.y * (x + EMIT);
  }

  // frac01 in [-1, 1] across the sheet's spread.
  vec3 sheetOrigin(float s, float frac01) {
    if (s < 0.5) {
      return vec3(-EMIT, uSheetPos.x + frac01 * uSpan.x, Z_AB + uShear.x * frac01);
    }
    if (s < 1.5) {
      return vec3(EMIT, uSheetPos.y + frac01 * uSpan.x, Z_AB + uShear.y * frac01);
    }
    float x = uSheetPos.z + frac01 * uSpan.y;
    return vec3(x, ${CURTAIN_TOP_Y.toFixed(1)}, curtainZAt(x) + uShear.z * frac01);
  }

  // Per BEAM, not per sheet: an A/B beam ends exactly where it meets the
  // back wall, wherever the shear stagger put its origin. dir.z = -SIN_A for
  // both stacks, so the ray solve reduces to one divide.
  float sheetLen(float s, vec3 origin) {
    if (s < 1.5) return (origin.z - ${WALL_Z.toFixed(1)}) / SIN_A;
    return ${BEAM_LEN_C.toFixed(1)};
  }

  // Dynamic [] on a vec3 is only guaranteed in vertex shaders and some
  // drivers are stricter than the spec - branch instead.
  float sheetGainOf(float s) {
    if (s < 0.5) return uSheetGain.x;
    if (s < 1.5) return uSheetGain.y;
    return uSheetGain.z;
  }

  // COMB: a playhead beam with a short comet behind it, everything else dark.
  // The head advances exactly one beam per beat (see update()).
  float combGain(float s, float idx) {
    if (uCombSheet < -0.5) return 1.0;
    if (abs(s - uCombSheet) > 0.5) return 0.0;
    float behind = uCombHead - idx;
    if (behind < 0.0) return 0.0;
    return exp(-behind * ${COMB_TRAIL_DECAY.toFixed(2)});
  }
`;

function makeBeamVertexShader(mirrored) {
  return `
  precision highp float;

  attribute float iSheet;
  attribute float iIndex;
  attribute float iSeed;
  uniform float uMasterGain;

  varying float vSide;
  varying float vLen;
  varying vec3 vColor;
  varying float vSeed;
  varying float vGain;
  varying float vSafeAtt;

  uniform vec3 uColor0;
  uniform vec3 uColor1;
  uniform vec3 uColor2;

  ${SHEET_CHUNK}
  ${BILLBOARD_CHUNK}
  ${SAFE_ZONE_VERTEX_CHUNK}

  vec3 sheetColor(float s) {
    if (s < 0.5) return uColor0;
    if (s < 1.5) return uColor1;
    return uColor2;
  }

  void main() {
    float frac01 = uBeamsPerSheet > 1.0
      ? (iIndex / (uBeamsPerSheet - 1.0)) * 2.0 - 1.0
      : 0.0;
    float activeGate = step(iIndex, uBeamsPerSheet - 0.5);
    vec3 origin = sheetOrigin(iSheet, frac01);
    vec3 dir = sheetDir(iSheet);
    ${mirrored ? `
    // The floor's virtual image: everything mirrored through y = 0.
    origin.y = -origin.y;
    dir = vec3(dir.x, -dir.y, dir.z);` : ''}

    float t = position.y;               // 0 at the source, 1 at the far end
    vec3 along = origin + dir * (t * sheetLen(iSheet, origin));

    float guard;
    vec3 worldPos = laserBillboard(along, dir, t, position.x, guard);

    vSide = position.x;
    vLen = t;
    vSeed = iSeed;
    vColor = sheetColor(iSheet);
    vGain = uMasterGain * sheetGainOf(iSheet) * combGain(iSheet, iIndex)
          * guard * activeGate${mirrored ? ` * ${REFLECT_GAIN.toFixed(2)}` : ''};

    gl_Position = projectionMatrix * modelViewMatrix * vec4(worldPos, 1.0);
    vSafeAtt = safeZoneAttenuation(gl_Position);
  }
`;
}

const beamFragmentShader = laserBeamFragmentShader({
  hotMix: BEAM_HOT_MIX,
  peakLuminance: PEAK_LUMINANCE,
  fadeStart: BEAM_FADE_START,
  fadeEnd: BEAM_FADE_END
});

// --- intersections ----------------------------------------------------------
// Slot layout: pair = slot / N^2 (0 = against sheet A, 1 = against B), then
// k = stack beam (height), j = curtain beam (x). The dot sits at the curtain
// beam's x and depth, at the stack beam's height, and lights by how close
// that point is to the stack's own slanted plane - zero exactly when the two
// beams truly cross.
function makePointsVertexShader(mirrored) {
  return `
  precision highp float;

  attribute float aSlot;
  uniform float uMasterGain;
  uniform float uPixelScaleP;
  uniform float uPointSizeMax;
  uniform float uHigh;

  varying vec3 vColor;
  varying float vGain;

  uniform vec3 uColor0;
  uniform vec3 uColor1;
  uniform vec3 uColor2;

  ${SHEET_CHUNK}

  void main() {
    float n2 = uBeamsPerSheet * uBeamsPerSheet;
    float pair = floor(aSlot / n2);
    float rem = aSlot - pair * n2;
    float k = mod(rem, uBeamsPerSheet);          // stack beam -> height
    float j = floor(rem / uBeamsPerSheet);       // curtain beam -> x, depth
    float slotGate = step(pair, 1.5)
                   * step(k, uBeamsPerSheet - 0.5) * step(j, uBeamsPerSheet - 0.5);

    float fk = uBeamsPerSheet > 1.0 ? (k / (uBeamsPerSheet - 1.0)) * 2.0 - 1.0 : 0.0;
    float fj = uBeamsPerSheet > 1.0 ? (j / (uBeamsPerSheet - 1.0)) * 2.0 - 1.0 : 0.0;

    float x = uSheetPos.z + fj * uSpan.y;
    float y = (pair < 0.5 ? uSheetPos.x : uSheetPos.y) + fk * uSpan.x;
    float z = curtainZAt(x);
    vec3 P = vec3(x, y, z);

    // The stack's slanted plane at this x - beams of A/B live exactly on it.
    float zPlane = pair < 0.5
      ? Z_AB - ${TAN_ANGLE.toFixed(6)} * (x + EMIT)
      : Z_AB - ${TAN_ANGLE.toFixed(6)} * (EMIT - x);
    float dz = abs(z - zPlane);
    float gate = 1.0 - smoothstep(0.0, ${INTERSECTION_TOL_Z.toFixed(2)}, dz);

    // The crossing has to sit inside both beams' real extents.
    float inY = step(0.05, y) * step(y, ${CURTAIN_TOP_Y.toFixed(1)});

    vColor = mix(uColor2, pair < 0.5 ? uColor0 : uColor1, 0.5);
    float sheetGate = sheetGainOf(pair) * uSheetGain.z;
    vGain = uMasterGain * uHigh * gate * sheetGate * inY * slotGate${mirrored ? ` * ${REFLECT_GAIN.toFixed(2)}` : ''};

    ${mirrored ? `P.y = -P.y;` : ''}
    vec4 mv = modelViewMatrix * vec4(P, 1.0);
    gl_PointSize = clamp(${INTERSECTION_WORLD_SIZE.toFixed(2)} * uPixelScaleP / max(-mv.z, 0.001),
                         1.0, uPointSizeMax);
    gl_Position = projectionMatrix * mv;
  }
`;
}

const pointsFragmentShader = `
  precision mediump float;

  uniform float uPulse;

  varying vec3 vColor;
  varying float vGain;

  ${CLAMP_CHUNK}

  void main() {
    vec2 uv = gl_PointCoord - vec2(0.5);
    float r = length(uv) * 2.0;
    if (r > 1.0) discard;
    // Near-white centre, the two sheets' mixed colour as the halo - the
    // brief's own description of what a crossing should look like.
    float core = 1.0 - smoothstep(0.0, 0.35, r);
    float h1 = 1.0 - r;
    float halo = h1 * h1;
    vec3 hot = mix(vColor, vec3(1.0), 0.92);
    vec3 col = mix(vColor * halo * 0.6, hot, core);
    col *= vGain * uPulse;
    gl_FragColor = vec4(clampLuminance(col), halo * min(vGain, 1.0));
  }
`;

// --- impacts ----------------------------------------------------------------
// A/B beams end ON the back wall (their length is derived from it): vertical
// quads standing against z = WALL_Z. Because a stack shares one direction and
// one origin depth, its dots form a vertical COLUMN at one x, riding the
// sweep. The curtain's beams end on the floor: the cathedral's flat quads.
const impactVertexShader = `
  precision highp float;

  attribute float iSheet;
  attribute float iIndex;
  uniform float uMasterGain;
  uniform float uWallGain;

  varying vec3 vColor;
  varying float vGain;
  varying vec2 vUv;

  uniform vec3 uColor0;
  uniform vec3 uColor1;
  uniform vec3 uColor2;

  ${SHEET_CHUNK}

  vec3 sheetColorI(float s) {
    if (s < 0.5) return uColor0;
    if (s < 1.5) return uColor1;
    return uColor2;
  }

  void main() {
    float frac01 = uBeamsPerSheet > 1.0
      ? (iIndex / (uBeamsPerSheet - 1.0)) * 2.0 - 1.0
      : 0.0;
    float activeGate = step(iIndex, uBeamsPerSheet - 0.5);
    vec3 origin = sheetOrigin(iSheet, frac01);
    vec3 dir = sheetDir(iSheet);

    vec3 hit;
    vec3 major;
    vec3 minor;
    float stretch;
    float within;
    if (iSheet < 1.5) {
      // The beam's far end IS the wall (length derived from it); the shear
      // stagger shifts the landing slightly, so re-solve exactly.
      float t = (${WALL_Z.toFixed(1)} - origin.z) / dir.z;
      hit = origin + dir * t;
      hit.z = ${WALL_Z.toFixed(1)} + 0.05;
      major = vec3(1.0, 0.0, 0.0);
      minor = vec3(0.0, 1.0, 0.0);
      // A grazing beam smears its dot across the wall along x.
      stretch = 1.0 / max(abs(dir.z), 0.15);
      // Wide enough for the SHEAR stagger's furthest landing (~14.6): the
      // wall is notional and this gate exists only against dots floating in
      // from a beam that never reached it at all.
      within = step(abs(hit.x), ${(EMIT_X + 2.5).toFixed(1)}) * step(0.05, hit.y);
    } else {
      hit = vec3(origin.x, 0.03, origin.z);
      major = vec3(1.0, 0.0, 0.0);
      minor = vec3(0.0, 0.0, 1.0);
      stretch = 1.0;
      within = 1.0;
    }

    vec3 worldPos = hit
                  + major * (position.x * ${IMPACT_BASE_SIZE.toFixed(2)} * stretch)
                  + minor * (position.z * ${IMPACT_BASE_SIZE.toFixed(2)});

    vUv = position.xz;
    vColor = sheetColorI(iSheet);
    vGain = uMasterGain * uWallGain * sheetGainOf(iSheet)
          * combGain(iSheet, iIndex) * within * activeGate;

    gl_Position = projectionMatrix * modelViewMatrix * vec4(worldPos, 1.0);
  }
`;

const impactFragmentShader = laserImpactFragmentShader({
  hotMix: IMPACT_HOT_MIX,
  peakLuminance: PEAK_LUMINANCE
});

// --- dust -------------------------------------------------------------------
// The cathedral tests each particle against eight representative beam lines.
// Here every sheet is a parallel lattice, so the nearest-beam distance is
// CLOSED FORM: distance to the stack's slanted plane, plus the mod-distance
// to the beam grid within it. No loop at all.
const dustVertexShader = `
  precision highp float;

  attribute float aSeed;
  uniform float uMasterGain;
  uniform float uPixelScaleP;
  uniform float uPointSizeMax;
  uniform float uDustTime;

  varying float vReveal;
  varying vec3 vColor;

  uniform vec3 uColor0;
  uniform vec3 uColor1;
  uniform vec3 uColor2;

  ${SHEET_CHUNK}

  float modDist(float v, float sp) {
    float m = mod(v + sp * 128.0, sp);
    return min(m, sp - m);
  }

  void main() {
    vec3 pos = position;
    pos.y += sin(uDustTime * 0.5 + aSeed * 6.2831) * 0.18;
    pos.x += sin(uDustTime * 0.3 + aSeed * 4.0) * 0.15;
    pos.z += cos(uDustTime * 0.25 + aSeed * 3.1) * 0.15;

    float n1 = max(uBeamsPerSheet - 1.0, 1.0);
    float spY = 2.0 * uSpan.x / n1;   // stack beam pitch, in height
    float spX = 2.0 * uSpan.y / n1;   // curtain beam pitch, in x

    // A: plane normal (SIN_A, 0, COS_A) through (-EMIT, ., Z_AB).
    float plA = abs((pos.x + EMIT) * SIN_A + (pos.z - Z_AB) * COS_A);
    float dA = length(vec2(plA, modDist(pos.y - uSheetPos.x, spY)));
    // B: the mirrored plane.
    float plB = abs((pos.x - EMIT) * SIN_A - (pos.z - Z_AB) * COS_A);
    float dB = length(vec2(plB, modDist(pos.y - uSheetPos.y, spY)));
    // C: depth off the (possibly tilted) curtain plane + x to its comb.
    float plC = abs(pos.z - curtainZAt(pos.x));
    float dC = length(vec2(plC, modDist(pos.x - uSheetPos.z, spX)));

    float rA = uSheetGain.x * (1.0 - smoothstep(0.0, ${DUST_REVEAL_RADIUS.toFixed(2)}, dA));
    float rB = uSheetGain.y * (1.0 - smoothstep(0.0, ${DUST_REVEAL_RADIUS.toFixed(2)}, dB));
    float rC = uSheetGain.z * (1.0 - smoothstep(0.0, ${DUST_REVEAL_RADIUS.toFixed(2)}, dC));
    float best = max(rA, max(rB, rC));
    vColor = rA >= rB && rA >= rC ? uColor0 : (rB >= rC ? uColor1 : uColor2);
    // A combed sheet only reveals along its lit beams - approximated by
    // dimming the whole field, which is what the eye tracks anyway.
    if (uCombSheet > -0.5) best *= 0.5;
    vReveal = best * uMasterGain;

    vec4 mv = modelViewMatrix * vec4(pos, 1.0);
    float worldSize = ${DUST_POINT_WORLD_SIZE.toFixed(3)} + aSeed * ${DUST_POINT_WORLD_VARIANCE.toFixed(3)};
    gl_PointSize = clamp(worldSize * uPixelScaleP / max(-mv.z, 0.001), 1.0, uPointSizeMax);
    gl_Position = projectionMatrix * mv;
  }
`;

const dustFragmentShader = laserDustFragmentShader({
  brightness: DUST_BRIGHTNESS,
  peakLuminance: PEAK_LUMINANCE
});

function urlParam(name) {
  if (typeof window === 'undefined' || !window.location) return null;
  return new URLSearchParams(window.location.search).get(name);
}

export class LaserGridVisualizer extends Visualizer {
  constructor(options) {
    super();
    const tier = options && options.qualityTier != null ? options.qualityTier : DEFAULT_TIER;
    this._tier = TIER_LAYOUT[tier] ? tier : DEFAULT_TIER;
    // ?gridmode=scan|grid|shear|comb|collapse pins one gesture.
    const forced = urlParam('gridmode');
    this._forcedMode = forced != null && MODE_NAMES[forced] !== undefined ? MODE_NAMES[forced] : null;
    // ?gridspread=<float> replaces SHEET_COVERAGE live.
    const spread = parseFloat(urlParam('gridspread'));
    this._coverage = Number.isFinite(spread) && spread > 0 ? spread : SHEET_COVERAGE;
  }

  init(scene, camera) {
    this._scene = scene;
    this._camera = camera;
    camera.position.set(CAMERA_POS[0], CAMERA_POS[1], CAMERA_POS[2]);
    camera.lookAt(CAMERA_LOOK[0], CAMERA_LOOK[1], CAMERA_LOOK[2]);

    scene.fog = new THREE.FogExp2(0x000000, FOG_DENSITY);
    this._ambient = new THREE.AmbientLight(0x141a20, 0.85);
    scene.add(this._ambient);

    const floorGeometry = new THREE.PlaneGeometry(FLOOR_SIZE, FLOOR_SIZE);
    const floorMaterial = new THREE.MeshStandardMaterial({
      color: FLOOR_COLOR,
      roughness: 0.78,
      metalness: 0.0
    });
    this._floor = new THREE.Mesh(floorGeometry, floorMaterial);
    this._floor.rotation.x = -Math.PI / 2;
    scene.add(this._floor);

    const layout = TIER_LAYOUT[this._tier];
    this._perSheet = layout.perSheet;
    this._sheetCount = layout.sheets;
    this._beamCount = this._perSheet * 3; // the third sheet's slots idle at
    // gain 0 when a tier has only two sheets - the cathedral's own
    // over-allocated-buffer pattern.

    this._uniforms = Object.assign({}, safeZoneUniforms, {
      uTime: { value: 0 },
      uMasterGain: { value: 1 },
      uPulse: { value: 1 },
      uOuterPx: { value: BEAM_OUTER_PX },
      uCorePx: { value: BEAM_CORE_PX },
      uPixelScale: { value: 1 },
      uPixelScaleP: { value: 1 },
      uPointSizeMax: { value: Math.max(1, getMaxPointSize()) },
      uBeamsPerSheet: { value: this._perSheet },
      uSheetPos: { value: new THREE.Vector3(EYE_Y + 1, EYE_Y - 1, 0) },
      uShear: { value: new THREE.Vector3(0, 0, 0) },
      uSpan: { value: new THREE.Vector2(3, 8) },
      uSheetGain: { value: new THREE.Vector3(1, 1, 0) },
      uCurtainZ: { value: new THREE.Vector2(CURTAIN_Z, 0) },
      uCombSheet: { value: -1 },
      uCombHead: { value: 0 },
      uWallGain: { value: 1 },
      uHigh: { value: 1 },
      uDustTime: { value: 0 },
      uColor0: { value: new THREE.Color(FALLBACK_COLORS[0]) },
      uColor1: { value: new THREE.Color(FALLBACK_COLORS[1]) },
      uColor2: { value: new THREE.Color(FALLBACK_COLORS[2]) }
    });

    this._buildBeams();
    this._buildImpacts();
    if (layout.intersections) this._buildIntersections();
    if (layout.dustCount > 0) this._buildDust(layout.dustCount);

    // --- gesture state ---
    this._time = 0;
    this._mode = this._forcedMode != null ? this._forcedMode : MODE_SCAN;
    this._lastAccentPhrase = null;
    this._lastModeChangeAt = 0;
    this._collapseStartBar = null;
    this._collapseT = 0;
    this._barIndex = 0;
    this._lastBarPhase = null;
    this._lastBeatPhase = null;
    this._combHead = 0;
    this._combSheet = 0;
    this._spacingLow = 0.5;
    this._high = 1;
    this._curtainGain = 0;
    // The curtain's pose (flat vs tilted onto A's plane) is smoothed so GRID
    // entering or leaving never snaps the comb through depth.
    this._curtainZBase = CURTAIN_Z;
    this._curtainSlope = 0;
    this._lastPaletteRef = undefined;
    this._scratch = new THREE.Color();
    this._hsl = { h: 0, s: 0, l: 0 };
    this._applyPalette(null);
  }

  // --- geometry builders ----------------------------------------------------

  _buildBeams() {
    const geometry = new THREE.InstancedBufferGeometry();
    geometry.setAttribute('position', new THREE.BufferAttribute(new Float32Array([
      -1, 0, 0, 1, 0, 0, 1, 1, 0, -1, 1, 0
    ]), 3));
    geometry.setIndex([0, 1, 2, 0, 2, 3]);

    const n = this._beamCount;
    const iSheet = new Float32Array(n);
    const iIndex = new Float32Array(n);
    const iSeed = new Float32Array(n);
    for (let i = 0; i < n; i++) {
      iSheet[i] = Math.floor(i / this._perSheet);
      iIndex[i] = i % this._perSheet;
      iSeed[i] = Math.random();
    }
    geometry.setAttribute('iSheet', new THREE.InstancedBufferAttribute(iSheet, 1));
    geometry.setAttribute('iIndex', new THREE.InstancedBufferAttribute(iIndex, 1));
    geometry.setAttribute('iSeed', new THREE.InstancedBufferAttribute(iSeed, 1));
    geometry.instanceCount = n;

    this._beamGeometry = geometry;
    this._beamMaterial = new THREE.ShaderMaterial({
      uniforms: this._uniforms,
      vertexShader: makeBeamVertexShader(false),
      fragmentShader: beamFragmentShader,
      transparent: true,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      depthTest: true
    });
    this._beams = new THREE.Mesh(geometry, this._beamMaterial);
    this._beams.frustumCulled = false;
    this._scene.add(this._beams);

    // The floor's virtual image of the beams (see the header). Same geometry,
    // mirrored shader; its uniforms object shares every holder with the main
    // pass except uOuterPx, widened because a rough floor smears what it
    // reflects. depthTest OFF: the virtual image sits below the floor plane,
    // and the floor's own depth would cull it to nothing.
    this._beamReflMaterial = new THREE.ShaderMaterial({
      uniforms: Object.assign({}, this._uniforms,
        { uOuterPx: { value: BEAM_OUTER_PX * REFLECT_OUTER_MULT } }),
      vertexShader: makeBeamVertexShader(true),
      fragmentShader: beamFragmentShader,
      transparent: true,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      depthTest: false
    });
    this._beamRefl = new THREE.Mesh(geometry, this._beamReflMaterial);
    this._beamRefl.frustumCulled = false;
    this._scene.add(this._beamRefl);
  }

  _buildImpacts() {
    const geometry = new THREE.InstancedBufferGeometry();
    geometry.setAttribute('position', new THREE.BufferAttribute(new Float32Array([
      -1, 0, -1, 1, 0, -1, 1, 0, 1, -1, 0, 1
    ]), 3));
    geometry.setIndex([0, 1, 2, 0, 2, 3]);
    const n = this._beamCount;
    const iSheet = new Float32Array(n);
    const iIndex = new Float32Array(n);
    for (let i = 0; i < n; i++) {
      iSheet[i] = Math.floor(i / this._perSheet);
      iIndex[i] = i % this._perSheet;
    }
    geometry.setAttribute('iSheet', new THREE.InstancedBufferAttribute(iSheet, 1));
    geometry.setAttribute('iIndex', new THREE.InstancedBufferAttribute(iIndex, 1));
    geometry.instanceCount = n;

    this._impactGeometry = geometry;
    this._impactMaterial = new THREE.ShaderMaterial({
      uniforms: this._uniforms,
      vertexShader: impactVertexShader,
      fragmentShader: impactFragmentShader,
      transparent: true,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      depthTest: true
    });
    this._impacts = new THREE.Mesh(geometry, this._impactMaterial);
    this._impacts.frustumCulled = false;
    this._scene.add(this._impacts);
  }

  _buildIntersections() {
    const slots = this._perSheet * this._perSheet * 2;
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.BufferAttribute(new Float32Array(slots * 3), 3));
    const aSlot = new Float32Array(slots);
    for (let i = 0; i < slots; i++) aSlot[i] = i;
    geometry.setAttribute('aSlot', new THREE.BufferAttribute(aSlot, 1));

    this._pointsGeometry = geometry;
    this._pointsMaterial = new THREE.ShaderMaterial({
      uniforms: this._uniforms,
      vertexShader: makePointsVertexShader(false),
      fragmentShader: pointsFragmentShader,
      transparent: true,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      depthTest: true
    });
    this._points = new THREE.Points(geometry, this._pointsMaterial);
    this._points.frustumCulled = false;
    this._scene.add(this._points);

    // Their reflection - same reasoning as the beams'.
    this._pointsReflMaterial = new THREE.ShaderMaterial({
      uniforms: this._uniforms,
      vertexShader: makePointsVertexShader(true),
      fragmentShader: pointsFragmentShader,
      transparent: true,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      depthTest: false
    });
    this._pointsRefl = new THREE.Points(geometry, this._pointsReflMaterial);
    this._pointsRefl.frustumCulled = false;
    this._scene.add(this._pointsRefl);
  }

  _buildDust(count) {
    const geometry = new THREE.BufferGeometry();
    const positions = new Float32Array(count * 3);
    const seeds = new Float32Array(count);
    for (let i = 0; i < count; i++) {
      positions[i * 3] = (Math.random() * 2 - 1) * DUST_FIELD.halfX;
      positions[i * 3 + 1] = lerp(DUST_FIELD.yMin, DUST_FIELD.yMax, Math.random());
      positions[i * 3 + 2] = lerp(DUST_FIELD.zMin, DUST_FIELD.zMax, Math.random());
      seeds[i] = Math.random();
    }
    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    geometry.setAttribute('aSeed', new THREE.BufferAttribute(seeds, 1));

    this._dustGeometry = geometry;
    this._dustMaterial = new THREE.ShaderMaterial({
      uniforms: this._uniforms,
      vertexShader: dustVertexShader,
      fragmentShader: dustFragmentShader,
      transparent: true,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      depthTest: true
    });
    this._dust = new THREE.Points(geometry, this._dustMaterial);
    this._dust.frustumCulled = false;
    this._scene.add(this._dust);
  }

  // --- palette --------------------------------------------------------------

  // One pure colour per sheet, hues as far apart as the cover offers.
  // Saturation is the brief's 0.95, always.
  _applyPalette(palette) {
    const vivid = (palette && palette.vividColors && palette.vividColors.length)
      ? palette.vividColors : FALLBACK_COLORS;
    const hues = [];
    for (let i = 0; i < vivid.length; i++) {
      this._scratch.set(vivid[i]).getHSL(this._hsl);
      hues.push(this._hsl.h);
    }
    let pick = [hues[0] || 0, hues[1 % hues.length] || 0.33, hues[2 % hues.length] || 0.66];
    if (hues.length >= 3) {
      let bestScore = -1;
      for (let a = 0; a < hues.length; a++) {
        for (let b = a + 1; b < hues.length; b++) {
          for (let c = b + 1; c < hues.length; c++) {
            const score = Math.min(
              hueDistance(hues[a], hues[b]),
              Math.min(hueDistance(hues[a], hues[c]), hueDistance(hues[b], hues[c]))
            );
            if (score > bestScore) { bestScore = score; pick = [hues[a], hues[b], hues[c]]; }
          }
        }
      }
    } else if (hues.length === 2) {
      pick = [hues[0], hues[1], (hues[0] + 0.5) % 1];
    } else if (hues.length === 1) {
      pick = [hues[0], (hues[0] + 0.33) % 1, (hues[0] + 0.66) % 1];
    }
    this._uniforms.uColor0.value.setHSL(pick[0], LASER_SATURATION, LASER_LIGHTNESS);
    this._uniforms.uColor1.value.setHSL(pick[1], LASER_SATURATION, LASER_LIGHTNESS);
    this._uniforms.uColor2.value.setHSL(pick[2], LASER_SATURATION, LASER_LIGHTNESS);
  }

  // --- gestures -------------------------------------------------------------

  _pickNextMode() {
    if (this._forcedMode != null) return this._forcedMode;
    const canCurtain = this._sheetCount >= 3 && this._curtainGain > 0.5;
    let total = 0;
    const pool = [];
    for (let i = 0; i < MODE_WEIGHTS.length; i++) {
      const entry = MODE_WEIGHTS[i];
      if (entry.mode === this._mode) continue;          // never repeat
      if (entry.mode === MODE_GRID && !canCurtain) continue;
      if (entry.mode === MODE_COLLAPSE && !canCurtain) continue;
      pool.push(entry);
      total += entry.w;
    }
    let r = Math.random() * total;
    for (let i = 0; i < pool.length; i++) {
      r -= pool[i].w;
      if (r <= 0) return pool[i].mode;
    }
    return pool.length ? pool[pool.length - 1].mode : MODE_SCAN;
  }

  _changeMode() {
    this._mode = this._pickNextMode();
    this._lastModeChangeAt = this._time;
    if (this._mode === MODE_COLLAPSE) this._collapseStartBar = this._barIndex;
    if (this._mode === MODE_COMB) {
      this._combSheet = (this._sheetCount >= 3 && this._curtainGain > 0.5) ? 2 : 0;
      this._combHead = 0;
    }
  }

  // barPhase in [0,1). Every gesture expresses its geometry as a function of
  // it, which is how the timing criteria hold by construction: SCAN's stacks
  // coincide exactly where sin(2*pi*barPhase) = 0 - the downbeat and beat 3,
  // both strong - and SHEAR's fan closes exactly at phase 0.
  _poseSheets(barPhase, dt, halfSpanY, halfSpanC) {
    const u = this._uniforms;
    const pos = u.uSheetPos.value;
    const shear = u.uShear.value;
    const gains = u.uSheetGain.value;

    // Defaults each frame; gestures override what they own.
    shear.set(0, 0, 0);
    u.uCombSheet.value = -1;
    let spanMulY = 1;
    let spanMulC = 1;
    let curtainTilt = 0;            // 1 = laid onto sheet A's plane (GRID)
    let gA = 1, gB = 1, gC = this._curtainGain;
    const slowDrift = Math.sin(this._time * 0.11);

    if (this._mode === MODE_SCAN) {
      const s = Math.sin(2 * Math.PI * barPhase);
      pos.x = SWEEP_MID + SWEEP_AMP * s;
      pos.y = SWEEP_MID - SWEEP_AMP * s;
      pos.z = LATERAL_AMP * 0.6 * slowDrift;
      gC = this._curtainGain * 0.35;   // depth, never competition
    } else if (this._mode === MODE_GRID) {
      // Posed, near still, and COPLANAR: the curtain lies onto sheet A's
      // slanted plane, so every crossing on screen is a true 3D crossing and
      // the whole intersection lattice lights.
      // Raised above eye level: the stack spans either side of its centre,
      // and centred on the eye it loses its lowest beams under the floor -
      // measured, that cost the pose a fifth of its vertical coverage.
      pos.x = SWEEP_MID + 0.7 + 0.06 * slowDrift;
      pos.y = SWEEP_MID;
      pos.z = 0.4 + 0.05 * Math.sin(this._time * 0.07);
      gB = 0;
      curtainTilt = 1;
    } else if (this._mode === MODE_SHEAR) {
      // One stack fans out in depth and squares back up; the closing lands on
      // the downbeat because (1 - cos) is zero exactly there.
      const fan = (1 - Math.cos(2 * Math.PI * barPhase)) * 0.5;
      pos.x = SWEEP_MID + 0.7;
      pos.y = SWEEP_MID - 0.9;
      pos.z = LATERAL_AMP * 0.4 * slowDrift;
      shear.x = fan * SHEAR_SPAN;
      gB = 0.55;
      gC = this._curtainGain * 0.3;
    } else if (this._mode === MODE_COMB) {
      pos.x = SWEEP_MID + 0.6;
      pos.y = SWEEP_MID - 0.6;
      pos.z = 0;
      u.uCombSheet.value = this._combSheet;
      if (this._combSheet === 2) { gA = 0; gB = 0; gC = Math.max(this._curtainGain, 0.001); }
      else { gB = 0; gC = 0; }
    } else if (this._mode === MODE_COLLAPSE) {
      // Two bars: everything converges on one point at the centre, then
      // blows outward. Span multipliers and heights are all it takes.
      const t = this._collapseT;
      if (t < 0.5) {
        const k = 1 - t * 2;                       // 1 -> 0
        pos.x = COLLAPSE_POINT_Y + SWEEP_AMP * k * 0.9;
        pos.y = COLLAPSE_POINT_Y - SWEEP_AMP * k * 0.9;
        pos.z = LATERAL_AMP * 0.5 * k * slowDrift;
        spanMulY = Math.max(k, 0.04);
        spanMulC = Math.max(k, 0.04);
      } else {
        const k = (t - 0.5) * 2;                   // 0 -> 1
        const burst = 1 + Math.sin(Math.PI * k) * (COLLAPSE_EXPLODE - 1);
        pos.x = COLLAPSE_POINT_Y + SWEEP_AMP * k * 0.8;
        pos.y = COLLAPSE_POINT_Y - SWEEP_AMP * k * 0.8;
        pos.z = 0;
        spanMulY = burst;
        spanMulC = burst;
      }
      gC = this._curtainGain;
    }

    u.uSpan.value.set(halfSpanY * spanMulY, halfSpanC * spanMulC);
    gains.set(gA, gB, gC);

    // The curtain glides between its flat plane and sheet A's slanted one.
    this._curtainZBase = approach(this._curtainZBase,
      curtainTilt > 0.5 ? Z_AB_ORIGIN : CURTAIN_Z, dt, TAU_CURTAIN_POSE);
    this._curtainSlope = approach(this._curtainSlope,
      curtainTilt > 0.5 ? -TAN_ANGLE : 0, dt, TAU_CURTAIN_POSE);
    u.uCurtainZ.value.set(this._curtainZBase, this._curtainSlope);
  }

  // --- frame ----------------------------------------------------------------

  update(deltaTime, profile) {
    if (profile.playbackState !== 'PLAYING') return; // gel

    this._time += deltaTime;
    const director = profile.director;
    const bands = (director && director.bands) || null;

    // Smoothed band inputs - immediate response reads as flicker.
    this._spacingLow = approach(this._spacingLow, bands ? clamp01(bands.low) : 0.5,
      deltaTime, TAU_SPACING);
    this._high = approach(this._high,
      lerp(HIGH_MIN, HIGH_MAX, bands ? clamp01(bands.high) : 0.5), deltaTime, TAU_HIGH);

    // The spread, from the LIVE camera: the correction's own formula,
    // spacing = coverage * visibleDim / (N - 1), expressed as half-spans.
    // Recomputed every frame, so a resize (aspect change) is automatically
    // right; bands.low modulates by +/-25% and can never zero it.
    const lowMul = 1 + (this._spacingLow - 0.5) * 2 * COVERAGE_SWING;
    const tanHalf = Math.tan(THREE.MathUtils.degToRad(this._camera.fov) / 2);
    const halfSpanY = 0.5 * this._coverage * lowMul * (2 * tanHalf * AB_REF_DEPTH);
    const halfSpanC = 0.5 * this._coverage * lowMul
      * (2 * tanHalf * C_REF_DEPTH * this._camera.aspect);

    // progress -> two sheets at the edges of the track, three through the
    // middle. The curtain FADES rather than switching.
    const progress = profile.duration > 0 ? clamp01(profile.progress) : 0.5;
    const wantCurtain = this._sheetCount >= 3 && Math.sin(Math.PI * progress) > 0.45 ? 1 : 0;
    this._curtainGain = approach(this._curtainGain, wantCurtain, deltaTime, TAU_CURTAIN_GAIN);

    // --- musical clocks ---
    let barPhase = director && director.bar != null ? director.bar : null;
    if (barPhase == null) {
      barPhase = (this._time % NO_GRID_BAR_S) / NO_GRID_BAR_S;
    }
    if (this._lastBarPhase != null && barPhase < this._lastBarPhase) this._barIndex++;
    this._lastBarPhase = barPhase;

    // COMB advances exactly one beam per beat - the beat's wrap is the step.
    const beatPhase = director && director.beat != null ? director.beat : null;
    if (this._mode === MODE_COMB) {
      const stepped = beatPhase != null
        ? (this._lastBeatPhase != null && beatPhase < this._lastBeatPhase)
        : (this._time - (this._lastCombStepAt || 0) >= NO_GRID_BEAT_S);
      if (stepped) {
        this._combHead = (this._combHead + 1) % this._perSheet;
        this._lastCombStepAt = this._time;
      }
    }
    this._lastBeatPhase = beatPhase;

    // Gesture changes ride the phrase accent (rising edge); without a grid, a
    // slow clock stands in.
    const accent = director ? director.accentPhrase : null;
    let change = false;
    if (accent != null) {
      if (this._lastAccentPhrase != null && accent > this._lastAccentPhrase) change = true;
      this._lastAccentPhrase = accent;
    } else if (this._time - this._lastModeChangeAt >= NO_GRID_MODE_S) {
      change = true;
    }
    // COLLAPSE owns its own duration: exactly two bars, then move on.
    if (this._mode === MODE_COLLAPSE) {
      const barsIn = (this._barIndex - this._collapseStartBar) + barPhase;
      this._collapseT = clamp01(barsIn / COLLAPSE_BARS);
      change = barsIn >= COLLAPSE_BARS;          // never cut a collapse short
    }
    if (change) this._changeMode();

    if (profile.artworkPalette !== this._lastPaletteRef) {
      this._lastPaletteRef = profile.artworkPalette;
      this._applyPalette(profile.artworkPalette);
    }

    this._poseSheets(barPhase, deltaTime, halfSpanY, halfSpanC);

    const u = this._uniforms;
    u.uTime.value = this._time;
    u.uDustTime.value = this._time;
    u.uHigh.value = this._high;
    u.uPulse.value = director ? director.pulse(BEAT_PULSE_AMOUNT, BEAT_PULSE_DECAY) : 1;
    u.uMasterGain.value = 1;
    u.uCombHead.value = this._combHead;

    // Pixels per world unit at unit view depth - the beams' width floor and
    // both point layers' sizes, same derivation as the cathedral's. Guarded
    // like urlParam above: the gesture logic runs under Node in its
    // acceptance harness, where there is no window and no pixels.
    const canvasPixelHeight = typeof window !== 'undefined'
      ? window.innerHeight * Math.min(window.devicePixelRatio || 1, 2)
      : 1080;
    const pixelScale = canvasPixelHeight / (2 * tanHalf);
    u.uPixelScale.value = pixelScale;
    u.uPixelScaleP.value = pixelScale;
  }

  dispose() {
    this._scene.fog = null;
    this._scene.remove(this._ambient);
    this._scene.remove(this._floor);
    this._floor.geometry.dispose();
    this._floor.material.dispose();
    this._scene.remove(this._beams);
    this._scene.remove(this._beamRefl);
    this._beamGeometry.dispose();      // shared by both beam passes
    this._beamMaterial.dispose();
    this._beamReflMaterial.dispose();
    this._scene.remove(this._impacts);
    this._impactGeometry.dispose();
    this._impactMaterial.dispose();
    if (this._points) {
      this._scene.remove(this._points);
      this._scene.remove(this._pointsRefl);
      this._pointsGeometry.dispose();  // shared by both point passes
      this._pointsMaterial.dispose();
      this._pointsReflMaterial.dispose();
      this._points = null;
      this._pointsRefl = null;
    }
    if (this._dust) {
      this._scene.remove(this._dust);
      this._dustGeometry.dispose();
      this._dustMaterial.dispose();
      this._dust = null;
    }
  }
}
